import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  EyeOff,
  MapPin,
  Briefcase,
  User,
  Phone,
  ChevronDown,
} from "lucide-react";
import axios from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { CareGiverContext } from "../context/CareGiverContext";
import { assets } from "../assets/assets";
import { SUBCITIES } from "../utils/constants";

const LoginDropdown = ({
  name,
  value,
  options,
  placeholder,
  onChange,
  hasError,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedOption = options.find((option) => option.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue, type: "select-one" } });
    setIsOpen(false);
  };

  return (
    <div className="relative dropdown-container" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className={`w-full flex items-center justify-between px-6 py-3.5 rounded-full border bg-accent dark:bg-dark-surface dark:text-dark-text text-sm outline-none focus:ring-2 transition-all text-left ${
          hasError
            ? "border-red-500 focus:ring-red-100"
            : "border-gray-200 dark:border-dark-border focus:border-primary dark:focus:border-primary focus:ring-primary/20"
        }`}
      >
        <span className={selectedOption ? "" : "text-gray-400 dark:text-dark-muted"}>
          {selectedOption?.label || placeholder}
        </span>
        <ChevronDown
          size={16}
          className={`text-primary transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-200">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={`w-full text-left px-4 py-2.5 rounded-lg text-sm transition-colors ${
                option.value === value
                  ? "bg-primary/10 text-primary font-semibold"
                  : "text-text dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Auth = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setCToken } = useContext(CareGiverContext);
  const backendUrl = import.meta.env.VITE_BACKEND_URL;
  const [isLogin, setIsLogin] = useState(true);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef(null);

  const languageOptions = [
    { code: "en", name: "English", short: "EN", flag: assets.ukFlag },
    { code: "am", name: "አማርኛ", short: "አማ", flag: assets.ethiopiaFlag },
    {
      code: "om",
      name: "Afaan Oromoo",
      short: "OM",
      flag: assets.ethiopiaFlag,
    },
    { code: "tg", name: "ትግርኛ", short: "ትግ", flag: assets.ethiopiaFlag },
  ];

  const currentLang =
    languageOptions.find(
      (l) => l.code === (i18n.language?.substring(0, 2) || "en"),
    ) || languageOptions[0];

  // Close language dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const [showPassword, setShowPassword] = useState(false);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    password: "",
    serviceCategory: "",
    experience: "",
    subcity: "",
    address: "",
    agreeTerms: false,
    agreeSafeguarding: false,
    confirmInfoAccurate: false,
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState(0); // 0-4
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch service categories on component mount
  useEffect(() => {
    const fetchServiceCategories = async () => {
      try {
        const response = await axios.get(
          `${backendUrl}/api/user/service-categories`,
        );
        if (response.data.success) {
          // API returns 'data' not 'categories'
          setServiceCategories(response.data.data || []);
        }
      } catch (error) {
        console.error("Failed to fetch service categories:", error);
        toast.error("Failed to load service categories");
      }
    };

    fetchServiceCategories();
  }, [backendUrl]);

  // Calculate Password Strength (for registration only)
  useEffect(() => {
    if (isLogin) return;
    const pwd = formData.password;
    let score = 0;
    if (pwd.length >= 8) score += 1;
    if (/[A-Z]/.test(pwd)) score += 1;
    if (/[0-9]/.test(pwd)) score += 1;
    if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
    setPasswordStrength(score);
  }, [formData.password, isLogin]);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "phone":
        if (!value) error = t("login.errors.phoneRequired");
        else if (!/^\d{10}$/.test(value)) error = t("login.errors.phoneDigits");
        break;
      case "password":
        if (!value) error = t("login.errors.passwordRequired");
        else if (!isLogin) {
          if (value.length < 8) error = t("login.errors.passwordMinLength");
        }
        break;
      case "fullName":
        if (!isLogin && !value.trim())
          error = t("login.errors.fullNameRequired");
        break;
      case "experience":
        if (!isLogin) {
          if (value === "") error = t("login.errors.experienceRequired");
          else if (
            value !== "below_1" &&
            (Number(value) < 0 || Number(value) > 8 || Number.isNaN(Number(value)))
          )
            error = t("login.errors.experienceRange");
        }
        break;
      case "serviceCategory":
        if (!isLogin && !value) error = t("login.errors.categoryRequired");
        break;
      case "subcity":
        if (!isLogin && !value) error = t("login.errors.subcityRequired");
        break;
      case "address":
        if (!isLogin && !value.trim())
          error = t("login.errors.addressRequired");
        break;
      case "agreeTerms":
        if (!isLogin && !value) error = t("login.errors.agreeTermsRequired");
        break;
      case "agreeSafeguarding":
        if (!isLogin && !value)
          error = t("login.errors.agreeSafeguardingRequired");
        break;
      case "confirmInfoAccurate":
        if (!isLogin && !value)
          error = t("login.errors.confirmAccuracyRequired");
        break;
    }
    return error;
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const val = type === "checkbox" ? e.target.checked : value;

    setFormData((prev) => ({ ...prev, [name]: val }));

    const error = validateField(name, val);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) newErrors[name] = error;
      else delete newErrors[name];
      return newErrors;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate fields based on mode
    Object.keys(formData).forEach((key) => {
      if (isLogin && !["phone", "password"].includes(key)) return; // Only validate phone & password for login
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Scroll to top of form if errors exist
      const formElement = document.getElementById("auth-form");
      if (formElement) formElement.scrollIntoView({ behavior: "smooth" });
      return;
    }

    setIsSubmitting(true);
    try {
      const url = `${backendUrl}/api/caregiver/auth/${
        isLogin ? "login" : "register"
      }`;
      const payload = isLogin
        ? {
            phoneNumber: formData.phone,
            password: formData.password,
          }
        : {
            name: formData.fullName,
            phoneNumber: formData.phone,
            password: formData.password,
            serviceCategoryId: formData.serviceCategory,
            experience: Number(
              formData.experience === "below_1" ? 0 : formData.experience,
            ),
            subcity: formData.subcity,
            addressLine1: formData.address,
          };
      const { data } = await axios.post(url, payload);

      if (data.success) {
        if (isLogin) {
          setCToken(data.token);
          localStorage.setItem("cToken", data.token);
          toast.success("Login successful! Welcome back.");
          navigate("/caregiver/dashboard");
        } else {
          toast.success(
            "Registration successful! Please login with your credentials.",
          );
          // Small delay to ensure toast is visible before form switches
          setTimeout(() => {
            toggleAuthMode();
          }, 500);
        }
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error(error);
      if (isLogin) {
        if (error.response) {
          if (error.response.status === 401) {
            toast.error("Invalid credentials. Please try again.");
          } else if (error.response.status === 403) {
            toast.error("Account deactivated. Please contact the super admin.");
          } else {
            toast.error("Login failed. Please try again.");
          }
        } else {
          toast.error("Login failed. Please try again.");
        }
      } else {
        toast.error("Registration failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleAuthMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setFormData({
      fullName: "",
      phone: "",
      password: "",
      serviceCategory: "",
      experience: "",
      subcity: "",
      address: "",
      agreeTerms: false,
      agreeSafeguarding: false, // Reset new checkbox states
      confirmInfoAccurate: false, // Reset new checkbox states
    });
  };

  const getStrengthLabel = () => {
    if (passwordStrength <= 1)
      return {
        label: "Weak",
        color: "text-red-500",
        barColor: "bg-red-500",
        width: "25%",
      };
    if (passwordStrength === 2)
      return {
        label: "Fair",
        color: "text-yellow-500",
        barColor: "bg-yellow-500",
        width: "50%",
      };
    if (passwordStrength === 3)
      return {
        label: "Good",
        color: "text-blue-500",
        barColor: "bg-blue-500",
        width: "75%",
      };
    return {
      label: "Strong",
      color: "text-emerald-500",
      barColor: "bg-emerald-500",
      width: "100%",
    };
  };

  const strengthInfo = getStrengthLabel();

  // Check if all required checkboxes are selected for signup
  const isCreateAccountDisabled =
    !isLogin &&
    (!formData.agreeTerms ||
      !formData.agreeSafeguarding ||
      !formData.confirmInfoAccurate);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 dark:from-dark-background dark:to-dark-surface flex items-center justify-center p-4 md:p-6 font-sans">
      <div
        className={`
          bg-card dark:bg-dark-surface w-full max-w-[1200px] min-h-[650px] rounded-[3rem] shadow-2xl overflow-hidden flex flex-col 
          transition-all duration-700 ease-in-out border border-gray-100 dark:border-dark-border
          ${isLogin ? "md:flex-row" : "md:flex-row-reverse"}
        `}
      >
        {/* Visual Panel (Image Side) */}
        <div className="w-full md:w-[50%] relative overflow-hidden hidden md:block group h-auto min-h-[650px]">
          {/* Dark overlay for better contrast */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/80 mix-blend-multiply z-10"></div>
          <div className="absolute inset-0 bg-primary/20 mix-blend-overlay z-10"></div>

          {/* Background Image from assets */}
          <img
            src={assets.not_found}
            alt="Mogzit In Home Care Plc"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
          />

          {/* Logo Placement */}
          <div className="absolute top-12 left-0 right-0 flex justify-center z-20">
            <img
              className="w-24 h-auto cursor-pointer"
              src={assets.admin_logo}
              alt="Mogzit Logo"
            />
          </div>

          {/* Text */}
          <div className="absolute bottom-20 left-12 right-12 text-center text-white z-20">
            <h2 className="text-3xl font-bold mb-4">
              {isLogin ? t("login.welcomeBack") : t("login.joinCommunity")}
            </h2>
            <p className="text-white/80 text-lg">
              {isLogin ? t("login.loginDesc") : t("login.registerDesc")}
            </p>
          </div>
        </div>

        {/* Form Panel */}
        <div className="flex-1 flex flex-col relative bg-card dark:bg-dark-surface h-[650px] overflow-hidden">
          {/* Language Switcher */}
          <div ref={langRef} className="absolute top-5 right-5 z-30">
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-200/80 dark:hover:bg-gray-700/80 transition-all text-sm font-semibold text-text dark:text-dark-text shadow-sm"
            >
              <img
                src={currentLang.flag}
                alt=""
                className="w-5 h-5 rounded-full object-cover ring-1 ring-gray-200 dark:ring-gray-600"
              />
              <span className="text-xs">{currentLang.short}</span>
              <ChevronDown
                size={14}
                className={`text-gray-400 transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`}
              />
            </button>

            {langOpen && (
              <div className="absolute right-0 mt-2 w-44 bg-card dark:bg-dark-surface rounded-2xl shadow-xl border border-gray-100 dark:border-dark-border overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200 z-50">
                {languageOptions.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      i18n.changeLanguage(lang.code);
                      setLangOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                      currentLang.code === lang.code
                        ? "bg-primary/10 text-primary"
                        : "text-text dark:text-dark-text hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                  >
                    <img
                      src={lang.flag}
                      alt=""
                      className="w-5 h-5 rounded-full object-cover ring-1 ring-gray-200 dark:ring-gray-600"
                    />
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Scrollable Content Container */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-8 md:p-12 lg:p-16 flex flex-col">
            <div className="max-w-md w-full mx-auto my-auto" id="auth-form">
              <h1 className="text-4xl md:text-5xl font-bold text-text dark:text-dark-text mb-3 tracking-tight">
                {isLogin ? t("login.login") : t("login.createAccount")}
              </h1>

              <p className="text-sm font-medium text-gray-500 dark:text-dark-muted mb-10">
                {isLogin
                  ? t("login.dontHaveAccount")
                  : t("login.alreadyHaveAccount")}
                <button
                  onClick={toggleAuthMode}
                  className="text-black dark:text-dark-text font-bold underline decoration-2 decoration-gray-300 dark:decoration-dark-border underline-offset-4 hover:decoration-primary transition-all"
                >
                  {isLogin ? t("login.createAnAccount") : t("login.login")}
                </button>
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* --- Personal Details Section --- */}
                {!isLogin && (
                  <div className="space-y-5 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-center gap-2 mb-2">
                      <User size={16} className="text-primary" />
                      <span className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider">
                        {t("login.personalDetails")}
                      </span>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-gray-700 dark:text-dark-muted ml-3 mb-1.5 block">
                        {t("login.fullName")}
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleChange}
                        placeholder={t("login.fullNamePlaceholder")}
                        className={`w-full px-6 py-3.5 rounded-full border bg-accent dark:bg-dark-surface dark:text-dark-text text-sm outline-none focus:ring-2 transition-all ${
                          errors.fullName
                            ? "border-red-500 focus:ring-red-100"
                            : "border-gray-200 dark:border-dark-border focus:border-primary dark:focus:border-primary focus:ring-primary/20"
                        }`}
                      />
                      {errors.fullName && (
                        <p className="text-red-500 text-xs ml-3 mt-1">
                          {errors.fullName}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* --- Login / General Fields --- */}
                <div className="space-y-5">
                  {isLogin && <div className="h-4"></div>}{" "}
                  {/* Spacer for login view */}
                  <div>
                    <label className="text-xs font-bold text-gray-700 dark:text-dark-muted ml-3 mb-1.5 block flex items-center gap-2">
                      <Phone size={16} />
                      {t("login.phoneNumber")}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="0911223344"
                      maxLength={10}
                      className={`w-full px-6 py-3.5 rounded-full border bg-accent dark:bg-dark-surface dark:text-dark-text text-sm outline-none focus:ring-2 transition-all ${
                        errors.phone
                          ? "border-red-500 focus:ring-red-100"
                          : "border-gray-200 dark:border-dark-border focus:border-primary dark:focus:border-primary focus:ring-primary/20"
                      }`}
                    />
                    {errors.phone && (
                      <p className="text-red-500 text-xs ml-3 mt-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-700 dark:text-dark-muted ml-3 mb-1.5 block">
                      {t("login.password")}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder={
                          isLogin
                            ? t("login.passwordPlaceholder")
                            : t("login.passwordMinChars")
                        }
                        className={`w-full pl-6 pr-14 py-3.5 rounded-full border bg-accent dark:bg-dark-surface dark:text-dark-text text-sm outline-none focus:ring-2 transition-all ${
                          errors.password
                            ? "border-red-500 focus:ring-red-100"
                            : "border-gray-200 dark:border-dark-border focus:border-primary dark:focus:border-primary focus:ring-primary/20"
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary dark:hover:text-dark-text"
                      >
                        {showPassword ? (
                          <EyeOff size={20} />
                        ) : (
                          <Eye size={20} />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-xs ml-3 mt-1">
                        {errors.password}
                      </p>
                    )}

                    {/* Password Strength Meter (Signup Only) */}
                    {!isLogin && formData.password && (
                      <div className="mt-2 ml-3 mr-3">
                        <div className="h-1 w-full bg-gray-200 dark:bg-dark-border rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-300 ${strengthInfo.barColor}`}
                            style={{ width: strengthInfo.width }}
                          ></div>
                        </div>
                        <p className="text-[10px] text-gray-500 dark:text-dark-muted mt-1 text-right">
                          {t("login.strength.label")}:{" "}
                          <span className={`font-bold ${strengthInfo.color}`}>
                            {strengthInfo.label === "Weak"
                              ? t("login.strength.weak")
                              : strengthInfo.label === "Fair"
                                ? t("login.strength.fair")
                                : strengthInfo.label === "Good"
                                  ? t("login.strength.good")
                                  : t("login.strength.strong")}
                          </span>
                        </p>
                      </div>
                    )}

                    {isLogin && (
                      <div className="flex justify-end pt-1 mr-2">
                        <button
                          type="button"
                          className="text-xs font-bold text-gray-900 dark:text-dark-text hover:text-primary dark:hover:text-primary hover:underline"
                        >
                          {t("login.forgotPassword")}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* --- Professional & Location Section (Signup Only) --- */}
                {!isLogin && (
                  <div className="space-y-6 animate-in fade-in slide-in-from-bottom-8 duration-700">
                    <div className="h-px bg-gray-100 dark:bg-dark-border my-2"></div>

                    {/* Professional */}
                    <div className="space-y-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Briefcase size={16} className="text-primary" />
                        <span className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider">
                          {t("login.professionalInfo")}
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2 md:col-span-1">
                          <label className="text-xs font-bold text-gray-700 dark:text-dark-muted ml-3 mb-1.5 block">
                            {t("login.serviceCategory")}
                          </label>
                          <LoginDropdown
                            name="serviceCategory"
                            value={formData.serviceCategory}
                            options={serviceCategories.map((cat) => ({
                              value: cat.id,
                              label: cat.label,
                            }))}
                            placeholder={t("login.select")}
                            onChange={handleChange}
                            hasError={errors.serviceCategory}
                          />
                          {errors.serviceCategory && (
                            <p className="text-red-500 text-xs ml-3 mt-1">
                              {errors.serviceCategory}
                            </p>
                          )}
                        </div>

                        <div className="col-span-2 md:col-span-1">
                          <label className="text-xs font-bold text-gray-700 dark:text-dark-muted ml-3 mb-1.5 block">
                            {t("login.experience")}
                          </label>
                          <LoginDropdown
                            name="experience"
                            value={formData.experience}
                            options={[
                              { value: "0", label: "No experience" },
                              { value: "below_1", label: "Below 1 year" },
                              ...Array.from({ length: 8 }, (_, index) => {
                                const year = index + 1;
                                return {
                                  value: String(year),
                                  label: `${year} ${year === 1 ? "year" : "years"}`,
                                };
                              }),
                            ]}
                            placeholder={t("login.experiencePlaceholder")}
                            onChange={handleChange}
                            hasError={errors.experience}
                          />
                          {errors.experience && (
                            <p className="text-red-500 text-xs ml-3 mt-1">
                              {errors.experience}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Location */}
                    <div className="space-y-5">
                      <div className="flex items-center gap-2 mb-2 pt-2">
                        <MapPin size={16} className="text-primary" />
                        <span className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider">
                          {t("login.location")}
                        </span>
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-dark-muted ml-3 mb-1.5 block">
                          {t("login.subcity")}
                        </label>
                        <LoginDropdown
                          name="subcity"
                          value={formData.subcity}
                          options={SUBCITIES.map((subcity) => ({
                            value: subcity,
                            label: subcity,
                          }))}
                          placeholder={t("login.selectSubcity")}
                          onChange={handleChange}
                          hasError={errors.subcity}
                        />
                        {errors.subcity && (
                          <p className="text-red-500 text-xs ml-3 mt-1">
                            {errors.subcity}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="text-xs font-bold text-gray-700 dark:text-dark-muted ml-3 mb-1.5 block">
                          {t("login.primaryAddress")}
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                          placeholder={t("login.addressPlaceholder")}
                          className={`w-full px-6 py-3.5 rounded-full border bg-accent dark:bg-dark-surface dark:text-dark-text text-sm outline-none focus:ring-2 transition-all ${
                            errors.address
                              ? "border-red-500"
                              : "border-gray-200 dark:border-dark-border focus:border-primary dark:focus:border-primary"
                          }`}
                        />
                        {errors.address && (
                          <p className="text-red-500 text-xs ml-3 mt-1">
                            {errors.address}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Terms and Additional Checkboxes */}
                {!isLogin && (
                  <div className="space-y-4 pt-4">
                    {/* Safeguarding Checkbox */}
                    <div className="flex items-start gap-3">
                      <div className="relative flex items-center">
                        <input
                          id="safeguarding"
                          name="agreeSafeguarding"
                          type="checkbox"
                          checked={formData.agreeSafeguarding}
                          onChange={handleChange}
                          className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer accent-primary dark:accent-primary"
                        />
                      </div>
                      <label
                        htmlFor="safeguarding"
                        className="text-xs font-medium text-gray-500 dark:text-dark-muted cursor-pointer"
                      >
                        {t("login.agreeSafeguardingOnly.prefix")}{" "}
                        <a
                          href="https://mogzit.com/safeguarding"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-text dark:text-dark-text underline decoration-gray-300 dark:decoration-dark-border hover:decoration-primary transition-all"
                        >
                          {t("login.agreeSafeguardingOnly.safeguarding")}
                        </a>
                      </label>
                    </div>
                    {errors.agreeSafeguarding && (
                      <p className="text-red-500 text-xs ml-1 -mt-2">
                        {errors.agreeSafeguarding}
                      </p>
                    )}

                    {/* Information Confirmation Checkbox */}
                    <div className="flex items-start gap-3">
                      <div className="relative flex items-center">
                        <input
                          id="info-confirmation"
                          name="confirmInfoAccurate"
                          type="checkbox"
                          checked={formData.confirmInfoAccurate}
                          onChange={handleChange}
                          className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer accent-primary dark:accent-primary"
                        />
                      </div>
                      <label
                        htmlFor="info-confirmation"
                        className="text-xs font-medium text-gray-500 dark:text-dark-muted cursor-pointer"
                      >
                        {t("login.confirmAccuracyFull")}
                      </label>
                    </div>
                    {errors.confirmInfoAccurate && (
                      <p className="text-red-500 text-xs ml-1 -mt-2">
                        {errors.confirmInfoAccurate}
                      </p>
                    )}

                    {/* Original Terms Checkbox (kept for backward compatibility if needed) */}
                    <div className="flex items-center gap-3">
                      <div className="relative flex items-center">
                        <input
                          id="terms"
                          name="agreeTerms"
                          type="checkbox"
                          checked={formData.agreeTerms}
                          onChange={handleChange}
                          className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer accent-primary dark:accent-primary"
                        />
                      </div>
                      <label
                        htmlFor="terms"
                        className="text-xs font-medium text-gray-500 dark:text-dark-muted cursor-pointer"
                      >
                        {t("login.agreeTermsFull.prefix")}{" "}
                        <a
                          href="https://mogzit.com/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-bold text-text dark:text-dark-text underline decoration-gray-300 dark:decoration-dark-border hover:decoration-primary transition-all"
                        >
                          {t("login.agreeTermsFull.terms")}
                        </a>
                      </label>
                    </div>
                    {errors.agreeTerms && (
                      <p className="text-red-500 text-xs ml-1 -mt-2">
                        {errors.agreeTerms}
                      </p>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting || isCreateAccountDisabled}
                  className={`w-full py-4 mt-6 text-white rounded-full font-bold text-sm hover:scale-[1.01] transition-all transform active:scale-[0.98] shadow-xl flex justify-center items-center gap-2 ${
                    isCreateAccountDisabled
                      ? "bg-gray-400 dark:bg-gray-600 cursor-not-allowed shadow-none"
                      : "bg-primary hover:bg-primary/90 shadow-primary/20 dark:shadow-none"
                  }`}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : isLogin ? (
                    t("login.login")
                  ) : (
                    t("login.createAccount")
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
